import { Box, Paper } from "@mui/material";
import { Ticket, TicketStatusEnum, User } from "../../../services/swagger";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TicketService, UserService } from "../../../services/api";
import useRefresh from "../../../hooks/useRefresh";
import { toast } from "react-toastify";
import ItemList from "../../molecules/ItemList";
import GroupIcon from "@mui/icons-material/Group";

export interface TicketAssigneesProps {
  ticket: Ticket;
}

export default function TicketAssignees(props: TicketAssigneesProps) {
  const { t } = useTranslation("Ticket");
  const { ticket } = props;
  const [assignedUsers, setAssignedUsers] = useState<string[]>([]);
  const [userList, setUserList] = useState<User[]>([]);
  const refresh = useRefresh();
  const loadUsers = async () => {
    const res = await UserService.getPrivilegedUserList({
      ignoreDeleted: true,
    });
    if (res?.data?.data) {
      setUserList(res?.data?.data);
    }
  };

  const handleChange = async (list: string[]) => {
    const oldList = [...assignedUsers]; // actual state
    setAssignedUsers(list); // 🔄 Optimistic update

    if (!ticket.department?._id)
      return toast(t("MustSelectDepartment"), { type: "error" });
  };

  const handleAssignUser = async (userId: string) => {
    if (!ticket._id) return;
    const res = await TicketService.createTicketAssignee({
      ticketId: ticket._id,
      createTicketAssigneeBody: {
        userId: userId,
      },
    });
    if (res?.data?.success) {
      refresh();
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!ticket._id) return;
    const res = await TicketService.deleteTicketAssignee({
      ticketId: ticket._id,
      userId: userId,
    });
    if (res?.data?.success) refresh();
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setAssignedUsers(ticket.assignees.map((assignee) => assignee._id || ""));
  }, [ticket]);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: (theme) => (theme.shape.borderRadius as number) / 8,
      }}
    >
      <ItemList
        unique
        disabled={
          ticket.status === TicketStatusEnum.PendingConfirmation ||
          ticket.status === TicketStatusEnum.Complete
        }
        dragDisabled
        items={assignedUsers}
        onChange={handleChange}
        onAdd={handleAssignUser}
        onDelete={handleDeleteUser}
        renderItem={(value) =>
          userList.find((user) => user._id === value)?.name
        }
        addList={userList
          .filter((user) => !assignedUsers.includes(user._id as string))
          .map((user) => ({
            name: user.name,
            value: user._id || "",
          }))}
        emptyLabel={t("NoAssigneesYet")}
        label={t("Assignees")}
        confirmAddition
        confirmDeletion
        labelIcon={GroupIcon}
      />
    </Box>
  );
}
